export const organicPestControlAlgorithm = async (ctx) => {
    const {
        gardenMatrix,
        plants,
        config,
        utils: { validatePlacement, placePlant, createPlantMap, fillRemainingOpenAreas, fetchOrganicPestControlPlants }
    } = ctx;

    if (!Array.isArray(gardenMatrix) || !Array.isArray(gardenMatrix[0])) {
        throw new Error('organicPestControlAlgorithm expects gardenMatrix to be a 2D array.');
    }

    const rows = gardenMatrix.length;
    const cols = gardenMatrix[0].length;
    const totalSoilCells = gardenMatrix.flat().filter(cell => cell === 1).length;

    let plantMap = createPlantMap(rows, cols);
    let plantsPlaced = 0;
    const warnings = [];

    // Track remaining counts with unique keys
    const plantCounts = {};
    plants.forEach((plant, index) => {
        const uniqueKey = `${plant.id}_${index}`;
        plantCounts[uniqueKey] = plant.count ?? Infinity;
    });

    // Normalize user plants with mapped properties
    const normalizedPlants = plants.map((plant, index) => ({
        ...plant,
        name: plant.plant_name || plant.name,
        image: plant.plant_image_url || plant.image,
        veggieImage: plant.plant_image_url || plant.veggieImage,
        plantingWidth: plant.planting_width || plant.plantingWidth || 1,
        plantingHeight: plant.planting_height || plant.plantingHeight || 1,
        uniqueKey: `${plant.id}_${index}`
    }));

    // Fetch organic pest control plants
    const pestControlPlants = await fetchOrganicPestControlPlants();

    if (!pestControlPlants || pestControlPlants.length === 0) {
        warnings.push('No organic pest control plants available. Using only user-selected plants.');
        // Fall back to regular planting without pest control
        return await fallbackToRegularPlanting();
    }

    // Normalize pest control plants
    const normalizedPestPlants = pestControlPlants.map(plant => ({
        ...plant,
        name: plant.plant_name || plant.name,
        image: plant.plant_image_url || plant.image,
        veggieImage: plant.plant_image_url || plant.veggieImage,
        plantingWidth: plant.planting_width || plant.plantingWidth || 1,
        plantingHeight: plant.planting_height || plant.plantingHeight || 1,
        isPestControl: true
    }));

    // Configuration for pest control distribution
    const pestControlRatio = config.pestControlRatio || 0.2; // 20% of garden by default
    const minPestControlDistance = config.minSpacing || 5; // Minimum spacing between pest control plants
    const maxPestControlDistance = config.maxSpacing || 8; // Maximum spacing for coverage

    // Calculate how many pest control plants we want to place
    const targetPestControlCells = Math.floor(totalSoilCells * pestControlRatio);

    // Helper function to check if a position is too close to existing pest control plants
    const isTooCloseToExistingPestControl = (row, col, currentPlantMap, minDistance) => {
        for (let r = Math.max(0, row - minDistance); r <= Math.min(rows - 1, row + minDistance); r++) {
            for (let c = Math.max(0, col - minDistance); c <= Math.min(cols - 1, col + minDistance); c++) {
                const existingPlant = currentPlantMap[r][c];
                if (existingPlant && existingPlant.isPestControl) {
                    const distance = Math.max(Math.abs(r - row), Math.abs(c - col));
                    if (distance < minDistance) {
                        return true;
                    }
                }
            }
        }
        return false;
    };

    // Helper function to find optimal positions for pest control plants
    const findPestControlPositions = (currentPlantMap) => {
        const positions = [];

        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                // Check each pest control plant size
                for (const pestPlant of normalizedPestPlants) {
                    if (validatePlacement(pestPlant, currentPlantMap, gardenMatrix, col, row)) {
                        // Check if this position provides good coverage without being too close to existing pest control
                        if (!isTooCloseToExistingPestControl(row, col, currentPlantMap, minPestControlDistance)) {
                            // Calculate coverage score (how well this position covers the garden)
                            let coverageScore = 0;
                            const checkRadius = maxPestControlDistance;

                            for (let r = Math.max(0, row - checkRadius); r < Math.min(rows, row + checkRadius); r++) {
                                for (let c = Math.max(0, col - checkRadius); c < Math.min(cols, col + checkRadius); c++) {
                                    if (gardenMatrix[r][c] === 1) { // Soil cell
                                        const distance = Math.max(Math.abs(r - row), Math.abs(c - col));
                                        // Closer cells get higher scores
                                        coverageScore += Math.max(0, checkRadius - distance);
                                    }
                                }
                            }

                            positions.push({
                                row,
                                col,
                                plant: pestPlant,
                                coverageScore,
                                // Add some randomness for variety
                                randomScore: Math.random()
                            });
                        }
                    }
                }
            }
        }

        // Sort by coverage score and randomness
        return positions.sort((a, b) => {
            const scoreA = a.coverageScore + (a.randomScore * 50);
            const scoreB = b.coverageScore + (b.randomScore * 50);
            return scoreB - scoreA;
        });
    };

    // Phase 1: Strategic placement of pest control plants
    let pestControlCellsPlaced = 0;
    let pestControlPositions = findPestControlPositions(plantMap);

    while (pestControlCellsPlaced < targetPestControlCells && pestControlPositions.length > 0) {
        const position = pestControlPositions.shift();

        // Double-check the position is still valid
        if (validatePlacement(position.plant, plantMap, gardenMatrix, position.col, position.row)) {
            plantMap = placePlant(plantMap, position.plant, position.row, position.col);
            plantsPlaced++;

            const plantArea = position.plant.plantingWidth * position.plant.plantingHeight;
            pestControlCellsPlaced += plantArea;

            warnings.push(`Placed ${position.plant.name} for pest control at (${position.row}, ${position.col})`);

            // Recalculate positions to avoid clustering
            if (pestControlCellsPlaced < targetPestControlCells) {
                pestControlPositions = findPestControlPositions(plantMap);
            }
        }
    }

    // Phase 2: Place user-selected plants in remaining spaces
    // Sort by size (largest first) for better space utilization
    const sortedUserPlants = [...normalizedPlants].sort((a, b) => {
        const areaA = a.plantingWidth * a.plantingHeight;
        const areaB = b.plantingWidth * b.plantingHeight;
        return areaB - areaA;
    });

    // Place user plants
    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
            for (const plant of sortedUserPlants) {
                const remaining = plantCounts[plant.uniqueKey];
                if (remaining <= 0) continue;

                if (validatePlacement(plant, plantMap, gardenMatrix, col, row)) {
                    plantMap = placePlant(plantMap, plant, row, col);
                    plantCounts[plant.uniqueKey]--;
                    plantsPlaced++;
                    break;
                }
            }
        }
    }

    // Phase 3: Fill any remaining gaps with additional pest control or user plants
    if (config.fillRemainingWithPestControl) {
        // Try to add more pest control plants in remaining spaces
        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                if (gardenMatrix[row][col] === 1 && plantMap[row][col] === null) {
                    // Randomly select a pest control plant
                    const randomPestPlant = normalizedPestPlants[Math.floor(Math.random() * normalizedPestPlants.length)];

                    if (validatePlacement(randomPestPlant, plantMap, gardenMatrix, col, row)) {
                        plantMap = placePlant(plantMap, randomPestPlant, row, col);
                        plantsPlaced++;
                        warnings.push(`Filled remaining space with ${randomPestPlant.name}`);
                    }
                }
            }
        }
    }

    // Collect unused plants
    const unusedPlants = normalizedPlants
        .filter(p => plantCounts[p.uniqueKey] > 0)
        .map(p => ({
            name: p.name,
            id: p.id,
            remaining: plantCounts[p.uniqueKey],
            plantingWidth: p.plantingWidth,
            plantingHeight: p.plantingHeight,
            reason: 'Space allocated for pest control or insufficient space'
        }));

    // Phase 4: Auto-fill if requested
    let filledMap = plantMap;
    if (config.autoFillEmpty) {
        try {
            filledMap = await fillRemainingOpenAreas(gardenMatrix, plantMap, config, plants);
            warnings.push('Filled remaining spaces with compatible plants');
        } catch (error) {
            warnings.push('Could not auto-fill remaining spaces');
        }
    }

    // Calculate final statistics
    const occupiedCells = filledMap.flat().filter(cell => cell !== null).length;
    const efficiency = totalSoilCells > 0 ? (occupiedCells / totalSoilCells) : 0;

    const pestControlCount = filledMap.flat().filter(cell => cell && cell.isPestControl).length;
    const actualPestControlRatio = totalSoilCells > 0 ? (pestControlCount / totalSoilCells) : 0;

    return {
        plantMap: filledMap,
        plantsPlaced,
        unusedPlants,
        warnings,
        efficiency,
        pestControlAnalysis: {
            pestControlPlantsPlaced: pestControlCount,
            targetRatio: pestControlRatio,
            actualRatio: actualPestControlRatio,
            coverage: actualPestControlRatio >= pestControlRatio * 0.8 ? 'Good' : 'Needs Improvement',
            availablePestControlTypes: normalizedPestPlants.length
        }
    };

    // Fallback function for when no pest control plants are available
    async function fallbackToRegularPlanting() {
        // Sort plants by area (largest first)
        const sortedPlants = [...normalizedPlants].sort((a, b) => {
            const areaA = a.plantingWidth * a.plantingHeight;
            const areaB = b.plantingWidth * b.plantingHeight;
            return areaB - areaA;
        });

        // Place plants normally
        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                for (const plant of sortedPlants) {
                    const remaining = plantCounts[plant.uniqueKey];
                    if (remaining <= 0) continue;

                    if (validatePlacement(plant, plantMap, gardenMatrix, col, row)) {
                        plantMap = placePlant(plantMap, plant, row, col);
                        plantCounts[plant.uniqueKey]--;
                        plantsPlaced++;
                        break;
                    }
                }
            }
        }

        const unusedPlants = normalizedPlants
            .filter(p => plantCounts[p.uniqueKey] > 0)
            .map(p => ({
                name: p.name,
                id: p.id,
                remaining: plantCounts[p.uniqueKey],
                plantingWidth: p.plantingWidth,
                plantingHeight: p.plantingHeight
            }));

        let filledMap = plantMap;
        if (config.autoFillEmpty) {
            filledMap = await fillRemainingOpenAreas(gardenMatrix, plantMap, config, plants);
        }

        const occupiedCells = filledMap.flat().filter(cell => cell !== null).length;
        const efficiency = totalSoilCells > 0 ? (occupiedCells / totalSoilCells) : 0;

        return {
            plantMap: filledMap,
            plantsPlaced,
            unusedPlants,
            warnings: ['No pest control plants available - used regular planting strategy'],
            efficiency,
            pestControlAnalysis: {
                pestControlPlantsPlaced: 0,
                targetRatio: 0,
                actualRatio: 0,
                coverage: 'None - No pest control plants available',
                availablePestControlTypes: 0
            }
        };
    }
};